import React, { useEffect, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, MagnifyingGlass, PencilSimple, Trash, IdentificationCard, MapPin } from "@phosphor-icons/react";
import { toast } from "sonner";

const EMPTY = {
  name: "", cpf: "", rg: "", phone: "", email: "", profession: "", monthly_income: "",
  cep: "", street: "", number: "", complement: "", neighborhood: "", city: "", state: "", address: "",
};

async function lookupCEP(rawCep) {
  const cep = (rawCep || "").replace(/\D/g, "");
  if (cep.length !== 8) return null;
  const res = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
  if (!res.ok) return null;
  const data = await res.json();
  if (data.erro) return null;
  return data;
}

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [cpfInfo, setCpfInfo] = useState(null);

  const onCpfBlur = async () => {
    const digits = (form.cpf || "").replace(/\D/g, "");
    if (digits.length !== 11) { setCpfInfo(null); return; }
    try {
      const { data } = await api.get(`/cpf/lookup?cpf=${digits}`);
      setCpfInfo(data);
      if (data.name && !form.name) setForm((f) => ({ ...f, name: data.name }));
    } catch { setCpfInfo(null); }
  };
  const [cepLoading, setCepLoading] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/clients${q ? `?q=${encodeURIComponent(q)}` : ""}`);
    setClients(data);
  };
  useEffect(() => { load(); }, [q]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...EMPTY, ...c, monthly_income: c.monthly_income || "" }); setOpen(true); };

  const onCepBlur = async () => {
    if (!form.cep || form.cep.replace(/\D/g, "").length !== 8) return;
    setCepLoading(true);
    try {
      const data = await lookupCEP(form.cep);
      if (data) {
        setForm((f) => ({
          ...f,
          street: data.logradouro || f.street,
          neighborhood: data.bairro || f.neighborhood,
          city: data.localidade || f.city,
          state: data.uf || f.state,
          complement: data.complemento || f.complement,
        }));
        toast.success("Endereço preenchido pelo CEP");
      } else {
        toast.error("CEP não encontrado");
      }
    } catch {
      toast.error("Erro ao consultar CEP");
    } finally { setCepLoading(false); }
  };

  const submit = async () => {
    if (!form.name || !form.cpf || !form.phone || !form.email) {
      toast.error("Preencha nome, CPF, telefone e email");
      return;
    }
    const payload = { ...form, monthly_income: form.monthly_income ? parseFloat(form.monthly_income) : null };
    try {
      if (editing) await api.put(`/clients/${editing.client_id}`, payload);
      else await api.post("/clients", payload);
      toast.success(editing ? "Cliente atualizado" : "Cliente cadastrado");
      setOpen(false); load();
    } catch { toast.error("Erro ao salvar"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir este cliente?")) return;
    await api.delete(`/clients/${id}`);
    toast.success("Cliente excluído");
    load();
  };

  return (
    <div className="p-8 max-w-[1400px]">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="eyebrow mb-1">Cadastro</div>
          <h1 className="font-display text-4xl tracking-tight">Clientes</h1>
          <p className="text-slate-600 mt-2">Cadastro central para inquilinos, fiadores e proprietários.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-client-btn" onClick={openNew} className="btn-primary"><Plus size={16} className="mr-2" /> Novo Cliente</Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Editar cliente" : "Novo cliente"}</DialogTitle></DialogHeader>

            {/* Dados pessoais */}
            <div className="eyebrow mt-2 mb-2">Dados pessoais</div>
            <div className="grid grid-cols-2 gap-3">
              <F label="Nome completo *"><Input data-testid="cli-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} /></F>
              <F label="CPF *">
                <Input data-testid="cli-cpf" value={form.cpf} onChange={(e) => setForm({...form, cpf: e.target.value})} onBlur={onCpfBlur} placeholder="000.000.000-00" />
                {cpfInfo && <div className={`text-[11px] mt-1 ${cpfInfo.valid ? "text-emerald-700" : "text-red-600"}`} data-testid="cli-cpf-info">{cpfInfo.valid ? `CPF válido${cpfInfo.name ? ` · nome encontrado: ${cpfInfo.name}` : ""}` : `CPF inválido — ${cpfInfo.reason}`}</div>}
              </F>
              <F label="RG"><Input data-testid="cli-rg" value={form.rg} onChange={(e) => setForm({...form, rg: e.target.value})} /></F>
              <F label="Telefone *"><Input data-testid="cli-phone" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} placeholder="(11) 98765-4321" /></F>
              <div className="col-span-2"><F label="Email *"><Input data-testid="cli-email" type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} /></F></div>
              <F label="Profissão"><Input value={form.profession} onChange={(e) => setForm({...form, profession: e.target.value})} /></F>
              <F label="Renda mensal"><Input type="number" value={form.monthly_income} onChange={(e) => setForm({...form, monthly_income: e.target.value})} /></F>
            </div>

            {/* Endereço */}
            <div className="flex items-center gap-2 mt-6 mb-2">
              <MapPin size={16} weight="duotone" color="#1E2A44" />
              <div className="eyebrow">Endereço</div>
              <div className="text-[10px] text-slate-400">— preenchimento automático via CEP</div>
            </div>
            <div className="grid grid-cols-6 gap-3">
              <div className="col-span-2">
                <F label="CEP">
                  <Input data-testid="cli-cep"
                    value={form.cep}
                    onChange={(e) => setForm({...form, cep: e.target.value})}
                    onBlur={onCepBlur}
                    placeholder={cepLoading ? "Buscando..." : "00000-000"}
                    disabled={cepLoading}
                  />
                </F>
              </div>
              <div className="col-span-4">
                <F label="Rua / Logradouro"><Input data-testid="cli-street" value={form.street} onChange={(e) => setForm({...form, street: e.target.value})} /></F>
              </div>
              <div className="col-span-2">
                <F label="Número"><Input data-testid="cli-number" value={form.number} onChange={(e) => setForm({...form, number: e.target.value})} /></F>
              </div>
              <div className="col-span-4">
                <F label="Complemento"><Input value={form.complement} onChange={(e) => setForm({...form, complement: e.target.value})} placeholder="Apto, bloco, referência" /></F>
              </div>
              <div className="col-span-3">
                <F label="Bairro"><Input value={form.neighborhood} onChange={(e) => setForm({...form, neighborhood: e.target.value})} /></F>
              </div>
              <div className="col-span-2">
                <F label="Cidade"><Input value={form.city} onChange={(e) => setForm({...form, city: e.target.value})} /></F>
              </div>
              <div className="col-span-1">
                <F label="UF"><Input maxLength={2} value={form.state} onChange={(e) => setForm({...form, state: e.target.value.toUpperCase()})} placeholder="SP" /></F>
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button data-testid="cli-submit" onClick={submit} className="btn-primary">Salvar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mb-4 relative max-w-md">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input data-testid="clients-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nome ou CPF" className="pl-10" />
      </div>

      <div className="card-flat overflow-hidden" data-testid="clients-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 eyebrow">
            <tr>
              <th className="text-left p-4">Nome</th>
              <th className="text-left p-4">CPF</th>
              <th className="text-left p-4">Telefone</th>
              <th className="text-left p-4">Email</th>
              <th className="text-left p-4">Cidade / UF</th>
              <th className="text-left p-4">Cadastrado</th>
              <th className="text-left p-4"></th>
            </tr>
          </thead>
          <tbody>
            {clients.map((c) => (
              <tr key={c.client_id} className="border-t border-[var(--line)] hover:bg-slate-50">
                <td className="p-4 font-medium">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-[var(--emerald-soft)] flex items-center justify-center">
                      <IdentificationCard size={16} color="#1E2A44" />
                    </div>
                    {c.name}
                  </div>
                </td>
                <td className="p-4 mono text-slate-600">{c.cpf}</td>
                <td className="p-4">{c.phone}</td>
                <td className="p-4 text-slate-600">{c.email}</td>
                <td className="p-4 text-slate-500">{c.city ? `${c.city}${c.state ? "/" + c.state : ""}` : "—"}</td>
                <td className="p-4 text-slate-500">{fmtDate(c.created_at)}</td>
                <td className="p-4 text-right">
                  <div className="flex gap-1 justify-end">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(c)} data-testid={`edit-${c.client_id}`}><PencilSimple size={14} /></Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(c.client_id)} data-testid={`del-${c.client_id}`}><Trash size={14} color="#DC2626" /></Button>
                  </div>
                </td>
              </tr>
            ))}
            {clients.length === 0 && (
              <tr><td colSpan="7" className="p-10 text-center text-slate-400">Nenhum cliente cadastrado. Clique em "Novo Cliente".</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
function F({ label, children }) {
  return <div><Label className="text-xs text-slate-500 mb-1.5 block">{label}</Label>{children}</div>;
}
